import { Box, Stack } from "@mui/material";
import type { QuestionnaireGroupDto, QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { useMemo } from "react";
import { useQuestionnaireContext } from "./QuestionnaireContext";
import { QuestionnaireQuestion } from "./QuestionnaireQuestion";
import { QuestionnaireSectionHeader } from "./QuestionnaireSectionHeader";
import type { InvestmentPlan } from "./types";
import type { RuleState } from "./useRuleEngine";

type Props = {
  group: QuestionnaireGroupDto;
  questions: QuestionnaireQuestionDto[];
  investmentPlans: InvestmentPlan[];
  ruleState: RuleState;
};

const getBlockQuestions = (questions: QuestionnaireQuestionDto[], blockId: number | undefined) =>
  questions.filter((q) => q.questionnaireGroupBlockID === blockId && q.parentQuestionID === null);

/**
 * Numbers every question of the questionnaire in one continuous run, so the count carries on across wizard
 * steps instead of restarting. Questions and groups hidden by the rules are skipped, leaving no gaps.
 */
const buildQuestionNumbering = (
  groups: QuestionnaireGroupDto[],
  questions: QuestionnaireQuestionDto[],
  ruleState: RuleState,
  investmentPlans: InvestmentPlan[],
) => {
  const order = new Map<number, number>();
  let position = 0;

  for (const group of groups) {
    if (!ruleState.isGroupVisible(group.questionnaireGroupID)) continue;

    for (const block of group.questionnaireGroupBlocks ?? []) {
      for (const question of getBlockQuestions(questions, block.questionnaireGroupBlockID)) {
        // A plan question is targeted per plan, so the plan-less check alone would drop it while it still renders.
        const isVisible =
          ruleState.isQuestionVisible(question.questionnaireQuestionID, null) ||
          (question.subType === "InvestmentPlan" &&
            investmentPlans.some((plan) =>
              ruleState.isQuestionVisible(question.questionnaireQuestionID, plan.nsureInvestmentPlanExternalId),
            ));
        if (!isVisible) continue;
        position += 1;
        order.set(question.questionnaireQuestionID, position);
      }
    }
  }

  return order;
};

/** Renders the blocks of a single questionnaire group - shared by the app wizard step and the PDF template. */
export const QuestionnaireBlocks = ({ group, questions, investmentPlans, ruleState }: Props) => {
  const { groups } = useQuestionnaireContext();
  const blocks = group.questionnaireGroupBlocks ?? [];

  const questionOrder = useMemo(
    () => buildQuestionNumbering(groups, questions, ruleState, investmentPlans),
    [groups, questions, ruleState, investmentPlans],
  );

  return (
    <Stack spacing={Spacing.XXL}>
      {blocks.map((block) => {
        const blockQuestions = getBlockQuestions(questions, block.questionnaireGroupBlockID);

        if (blockQuestions.length === 0) return null;

        return (
          <Box key={block.questionnaireGroupBlockID}>
            <QuestionnaireSectionHeader title={block.name} description={block.description} />
            <Stack pt={Spacing.XL} spacing={Spacing.XXL}>
              {blockQuestions.map((question) => {
                const hasOwnAnswer = question.type === "Text" || question.questionnaireQuestionItems.length > 0;
                const relatedQuestions = hasOwnAnswer
                  ? undefined
                  : questions.filter((q) => q.parentQuestionID === question.questionnaireQuestionID);

                return (
                  <QuestionnaireQuestion
                    key={question.questionnaireQuestionID}
                    question={question}
                    relatedQuestions={relatedQuestions}
                    investmentPlans={investmentPlans}
                    ruleState={ruleState}
                    externalId={null}
                    order={questionOrder.get(question.questionnaireQuestionID)}
                  />
                );
              })}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};
